import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { jobVacancyService } from '../../services/jobVacancyService';
import { JobApplication } from '../../types/jobVacancy';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface VacancyApplicationsProps {
    vacancyId: number;
    open: boolean;
    onClose: () => void;
}

const VacancyApplications: React.FC<VacancyApplicationsProps> = ({ vacancyId, open, onClose }) => {
    const { t } = useTranslation();
    const [applications, setApplications] = useState<JobApplication[]>([]);
    const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
    const [newStatus, setNewStatus] = useState('');
    const [feedback, setFeedback] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            loadApplications();
        }
    }, [open, vacancyId]);

    const loadApplications = async () => {
        try {
            const data = await jobVacancyService.getVacancyApplications(vacancyId);
            setApplications(data);
        } catch (error) {
            console.error('Error loading applications:', error);
        }
    };

    const handleUpdateStatus = async () => {
        if (!selectedApplication || !newStatus) return;

        setLoading(true);
        try {
            await jobVacancyService.updateApplicationStatus(selectedApplication.id, {
                status: newStatus,
                feedback
            });
            await loadApplications();
            handleCloseStatusDialog();
        } catch (error) {
            console.error('Error updating application status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseStatusDialog = () => {
        setSelectedApplication(null);
        setNewStatus('');
        setFeedback('');
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PENDING':
                return 'warning';
            case 'APPROVED':
                return 'success';
            case 'REJECTED':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'PENDING':
                return 'Pendente';
            case 'APPROVED':
                return 'Aprovado';
            case 'REJECTED':
                return 'Rejeitado';
            default:
                return status;
        }
    };

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Candidaturas</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Candidato</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Data</TableCell>
                                <TableCell>Currículo</TableCell>
                                <TableCell>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {applications.map((application) => (
                                <TableRow key={application.id}>
                                    <TableCell>{application.candidate_name}</TableCell>
                                    <TableCell>{application.candidate_email}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(application.status)}
                                            color={getStatusColor(application.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatDate(application.created_at)}</TableCell>
                                    <TableCell>
                                        <IconButton size="small" aria-label="Abrir currículo"
                                            onClick={() => jobVacancyService.openResume(application.resume_url).catch(() => alert('Não foi possível abrir o currículo'))}>
                                            <DownloadIcon />
                                        </IconButton>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            onClick={() => setSelectedApplication(application)}
                                            disabled={application.status !== 'PENDING'}
                                        >
                                            Avaliar
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <Dialog open={!!selectedApplication} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Avaliar Candidatura</DialogTitle>
                <DialogContent>
                    <Box mt={2}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                label="Status"
                            >
                                <MenuItem value="APPROVED">Aprovar</MenuItem>
                                <MenuItem value="REJECTED">Rejeitar</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            margin="normal"
                            label="Feedback"
                            multiline
                            rows={3}
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseStatusDialog}>Cancelar</Button>
                    <Button
                        onClick={handleUpdateStatus}
                        variant="contained"
                        disabled={!newStatus || loading}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default VacancyApplications; 